import { hashWords } from "../random.ts";
import { FOOTPRINT_DEFAULT_CELL } from "./footprint-cell.ts";
import type { FootprintOverride } from "./footprint-override.ts";

/**
 * The digest of a library entry's shape, which a footprint save names as the entry it was worked out against so the
 * library can refuse it when another save has changed that shape since (P1.14-T57). It covers the lists a save
 * replaces whole - `cells`, `passage`, `behind` and `front` - and the cell size they are counted in, and nothing
 * else: a flag, a surface, a depth, a band or a timing is not part of the shape. Pure integer maths over the shared
 * hash, so the editor, the studio, the dev server and the build server compute the same number from the same entry.
 */

const SEED = 0x6f70_7365;
const CELLS_ABSENT = 0;
const CELLS_PRESENT = 1;

/**
 * The words of one list of cells: the count, then each cell's column and row, in column then row order so the order a
 * list was written in does not matter.
 *
 * @param cells The list.
 * @param into The words to append to.
 */
function appendCells(cells: readonly (readonly [number, number])[], into: number[]): void {
  into.push(cells.length >>> 0);
  const sorted = [...cells].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  for (const [column, row] of sorted) {
    into.push(column >>> 0, row >>> 0);
  }
}

/**
 * The digest of an entry's shape. An absent entry, `none` (which holds an empty `cells` list), and an entry that holds
 * no shape digest by what the merge keeps of them: no entry and an entry of flags alone digest the same, `none` and
 * an empty `cells` list digest the same, and an absent `cells` list differs from an empty one because Auto and None are
 * different shapes. The cell size is counted only when some list holds a cell.
 *
 * @param entry The library's entry for the frame, or undefined when it holds none.
 * @returns An unsigned 32-bit integer.
 */
export function footprintShapeDigest(entry: FootprintOverride | null | undefined): number {
  const fields: Exclude<FootprintOverride, "none"> = entry === undefined || entry === null ? {} : entry === "none" ? { cells: [] } : entry;
  const words: number[] = [fields.cells === undefined ? CELLS_ABSENT : CELLS_PRESENT];
  const lists = [fields.cells ?? [], fields.passage ?? [], fields.behind ?? [], fields.front ?? []];
  for (const list of lists) {
    appendCells(list, words);
  }
  const held = lists.some((list) => list.length > 0);
  words.push(held ? (fields.cell ?? FOOTPRINT_DEFAULT_CELL) >>> 0 : 0);
  return hashWords(SEED, words, words.length);
}

const SHAPE_KEYS = ["cells", "passage", "behind", "front", "cell"] as const;

/**
 * Whether a change can replace what a frame's entry holds of cells, passage, Behind and In front: back to the derived
 * footprint, none, or an entry that names any of those lists or their cell size. A flag, a surface, a band or a timing
 * alone keeps the stored lists. A save of the first kind names the digest of the entry it was based on.
 *
 * @param override The change: an override, `none`, or null for the derived footprint.
 * @returns True when the change must name the digest of the entry it was based on.
 */
export function replacesFootprintShape(override: FootprintOverride | null): boolean {
  return override === null || override === "none" || SHAPE_KEYS.some((key) => key in override);
}
